export interface QueryStringOptions {
    /**
     * How to format arrays in the query string
     * 'comma' - foo=1,2,3
     * 'repeat' - foo=1&foo=2&foo=3
     * 'brackets' - foo[]=1&foo[]=2&foo[]=3
     */
    arrayFormat?: "comma" | "repeat" | "brackets";

    /**
     * Whether to encode URI components (default: true)
     */
    encode?: boolean;

    /**
     * Whether to include null/undefined values (default: false)
     */
    includeNull?: boolean;

    /**
     * Custom key transformer function
     */
    keyTransform?: (key: string) => string;

    /**
     * Custom value transformer function
     */
    valueTransform?: (value: any, key: string) => string;
}

export type QueryValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | (string | number | boolean)[];

export type QueryObject = Record<string, QueryValue>;

/**
 * Formats an object of properties into a URL query string
 *
 * @param params - Object containing key-value pairs to convert to query string
 * @param options - Configuration options for formatting
 * @returns Formatted query string (without leading '?')
 *
 * @example
 * ```typescript
 * // Basic usage
 * formatQueryString({ name: 'John', age: 30 })
 * // Returns: "name=John&age=30"
 *
 * // Array with comma format
 * formatQueryString({ ids: [1, 2, 3] }, { arrayFormat: 'comma' })
 * // Returns: "ids=1%2C2%2C3"
 *
 * // Array with repeat format
 * formatQueryString({ ids: [1, 2, 3] }, { arrayFormat: 'repeat' })
 * // Returns: "ids=1&ids=2&ids=3"
 *
 * // Array with brackets format
 * formatQueryString({ ids: [1, 2, 3] }, { arrayFormat: 'brackets' })
 * // Returns: "ids%5B%5D=1&ids%5B%5D=2&ids%5B%5D=3"
 * ```
 */
export function formatQueryString(
    params: QueryObject,
    options: QueryStringOptions = {},
): string {
    const {
        arrayFormat = "comma",
        encode = true,
        includeNull = false,
        keyTransform,
        valueTransform,
    } = options;

    const pairs: string[] = [];

    // Helper function to encode values if needed
    const encodeValue = (value: string): string => {
        return encode ? encodeURIComponent(value) : value;
    };

    // Helper function to encode keys if needed
    const encodeKey = (key: string): string => {
        return encode ? encodeURIComponent(key) : key;
    };

    // Process each key-value pair
    Object.entries(params).forEach(([key, value]) => {
        // Skip null/undefined values unless explicitly included
        if ((value === null || value === undefined) && !includeNull) {
            return;
        }

        // Apply key transformation if provided
        const transformedKey = keyTransform ? keyTransform(key) : key;

        // Handle different value types
        if (Array.isArray(value)) {
            handleArrayValue(
                transformedKey,
                value,
                arrayFormat,
                pairs,
                encodeKey,
                encodeValue,
                valueTransform,
            );
        } else {
            handleSingleValue(
                transformedKey,
                value,
                pairs,
                encodeKey,
                encodeValue,
                valueTransform,
            );
        }
    });

    return pairs.join("&");
}

/**
 * Handles array values based on the specified format
 */
function handleArrayValue(
    key: string,
    value: (string | number | boolean)[],
    arrayFormat: "comma" | "repeat" | "brackets",
    pairs: string[],
    encodeKey: (key: string) => string,
    encodeValue: (value: string) => string,
    valueTransform?: (value: any, key: string) => string,
): void {
    if (value.length === 0) return;

    switch (arrayFormat) {
        case "comma": {
            const commaSeparatedValues = value
                .map((v) =>
                    valueTransform ? valueTransform(v, key) : String(v),
                )
                .join(",");
            pairs.push(
                `${encodeKey(key)}=${encodeValue(commaSeparatedValues)}`,
            );
            break;
        }
        case "repeat":
            value.forEach((v) => {
                const transformedValue = valueTransform
                    ? valueTransform(v, key)
                    : String(v);
                pairs.push(
                    `${encodeKey(key)}=${encodeValue(transformedValue)}`,
                );
            });
            break;

        case "brackets": {
            const bracketKey = `${key}[]`;
            value.forEach((v) => {
                const transformedValue = valueTransform
                    ? valueTransform(v, key)
                    : String(v);
                pairs.push(
                    `${encodeKey(bracketKey)}=${encodeValue(transformedValue)}`,
                );
            });
            break;
        }
    }
}

/**
 * Handles single values (non-array)
 */
function handleSingleValue(
    key: string,
    value: QueryValue,
    pairs: string[],
    encodeKey: (key: string) => string,
    encodeValue: (value: string) => string,
    valueTransform?: (value: any, key: string) => string,
): void {
    let stringValue: string;

    // Apply value transformer if provided, regardless of value type
    if (valueTransform) {
        stringValue = valueTransform(value, key);
    } else {
        // Default handling for null/undefined when no transformer
        if (value === null) {
            stringValue = "";
        } else if (value === undefined) {
            stringValue = "";
        } else {
            stringValue = String(value);
        }
    }

    pairs.push(`${encodeKey(key)}=${encodeValue(stringValue)}`);
}

/**
 * Convenience function to create a complete URL with query string
 *
 * @param baseUrl - The base URL
 * @param params - Query parameters
 * @param options - Formatting options
 * @returns Complete URL with query string
 *
 * @example
 * ```typescript
 * createUrlWithQuery('https://api.example.com/users', { page: 1, limit: 10 })
 * // Returns: "https://api.example.com/users?page=1&limit=10"
 * ```
 */
export function createUrlWithQuery(
    baseUrl: string,
    params: QueryObject,
    options?: QueryStringOptions,
): string {
    const queryString = formatQueryString(params, options);

    if (!queryString) {
        return baseUrl;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${queryString}`;
}
